import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  RefObject,
  useMemo,
  useCallback,
} from 'react'
import { graphql, useStaticQuery, Link } from 'gatsby'
import { FileText } from '@stratascratch/icons'
import { cn, Loader } from '@stratascratch/ui'

interface SearchResult {
  title: string
  slug: string
}

const SearchDialog = forwardRef<HTMLDialogElement>(
  function SearchDialog(_, ref) {
    const recent = useStaticQuery<Queries.SearchRecentPostsQuery>(graphql`
      query SearchRecentPosts {
        allSanityPost(sort: { publishedAt: DESC }, limit: 8) {
          edges {
            node {
              title
              slug {
                current
              }
            }
          }
        }
      }
    `)
    const recentPosts = useMemo(
      () =>
        recent.allSanityPost.edges.map((p) => ({
          title: p.node.title || '',
          slug: p.node.slug?.current || '',
        })),
      [],
    )
    const [value, setValue] = useState('')
    const [data, setData] = useState<SearchResult[]>(recentPosts)
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState(0)
    const listRef = useRef<HTMLOListElement>(null)
    const timeoutRef = useRef<NodeJS.Timeout>(null)
    const controllerRef = useRef<AbortController>(null)

    const getSelected = useCallback(
      (index: number) => listRef.current?.querySelectorAll('a').item(index),
      [listRef],
    )

    const isVisible = useCallback((element: Element) => {
      const childRect = element.getBoundingClientRect()
      const parentRect = listRef.current?.parentElement?.getBoundingClientRect()

      return (
        parentRect &&
        childRect.top > parentRect.top &&
        childRect.bottom < parentRect.bottom
      )
    }, [])

    const scrollToSelected = useCallback(
      (index: number) => {
        const selectedEl = getSelected(index)
        if (selectedEl && !isVisible(selectedEl))
          selectedEl?.scrollIntoView({ block: 'nearest' })
      },
      [getSelected],
    )

    useEffect(() => {
      const controller = new AbortController()

      window.addEventListener(
        'keydown',
        (e) => {
          if (!(ref as RefObject<HTMLDialogElement>).current.open) return
          if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
            e.preventDefault()
            const newSelected = Math.min(selected + 1, data.length - 1)
            setSelected(newSelected)
            scrollToSelected(newSelected)
          } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
            e.preventDefault()
            const newSelected = Math.max(selected - 1, 0)
            setSelected(newSelected)
            scrollToSelected(newSelected)
          } else if (e.key === 'Enter') {
            e.preventDefault()
            listRef.current?.querySelectorAll('a').item(selected).click()
          }
        },
        { signal: controller.signal },
      )

      return () => controller.abort()
    }, [selected, setSelected, listRef])

    const fetchResults = async (query: string) => {
      if (!query) {
        setData(recentPosts)
        setSelected(0)
      }
      if (query.length < 2) return

      if (controllerRef.current) controllerRef.current.abort()

      setLoading(true)
      const results = await fetch(`/api/search?query=${query}`, {
        signal: controllerRef.current?.signal,
      })
      const data = await results.json()
      setLoading(false)

      if (data) {
        setData(data)
        setSelected(0)
      }
    }

    const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      setValue(e.target.value)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fetchResults(e.target.value)
      }, 300)
    }

    const closeSearch = () =>
      (ref as RefObject<HTMLDialogElement>).current.close()

    return (
      <dialog
        ref={ref}
        className="border-(--color-border) bg-(--bg-color) md:w-160 top-auto mx-auto w-full max-w-full border-t backdrop:bg-white/80 md:top-[20%] md:rounded-lg md:border"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeSearch()
        }}
      >
        <div className="border-(--color-border) flex items-center gap-2 border-b py-2 pe-4 ps-2">
          <input
            type="text"
            placeholder="Search"
            className="h-8 w-full px-2 outline-transparent"
            value={value}
            onChange={onChange}
          />
          <Loader className={cn('shrink-0', !loading && 'opacity-0')} />
          <button
            type="button"
            className="cursor-pointer"
            aria-label="Close search"
            onClick={closeSearch}
          >
            <kbd className="border-(--color-border) ms-auto rounded-sm border bg-white px-1 py-0.5 font-sans text-xs text-black transition-colors">
              Esc
            </kbd>
          </button>
        </div>
        <div className="md:max-h-128 h-[60vh] overflow-y-auto md:h-auto">
          {data.length > 0 ? (
            <ol className="p-2" ref={listRef}>
              {data.map((item, index) => (
                <li key={item.slug}>
                  <Link
                    to={`/blog/${item.slug}`}
                    className={cn(
                      'focus:bg-(--bg-color-primary) active:bg-(--bg-color-primary) flex scroll-my-2 items-center gap-4 rounded-lg bg-inherit p-3',
                      index === selected &&
                        'pointer-fine:bg-(--bg-color-primary)',
                    )}
                    onMouseEnter={() => setSelected(index)}
                  >
                    <FileText className="text-(--text-color-200) shrink-0" />
                    <span className="text-(--text-color-light) truncate text-sm">
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <div className="px-4 py-8 text-center">No results found</div>
          )}
        </div>
      </dialog>
    )
  },
)

export default SearchDialog
